import { useEffect, useRef, useState, type ComponentType, type ReactNode } from 'react';
import { R, ft } from '../lib/ui';
import { AnchoredPopover } from './AnchoredPopover';
import type { IconProps } from './Primitives';

/** Кнопка панели: только иконка, иногда с подписью, с состоянием «включено». */
export function ToolButton({
  icon: Icon, label, title, active, disabled, onClick,
}: {
  icon: ComponentType<IconProps>;
  label?: string;
  title: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      title={title}
      disabled={disabled}
      onClick={onClick}
      className={`rt-toolbtn ${active ? 'rt-toolbtn--active' : ''}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        height: 28,
        padding: label ? '0 9px' : 0,
        width: label ? undefined : 28,
        justifyContent: 'center',
        fontSize: 12.5,
        color: disabled ? R.textMuted : active ? R.accent : R.textSecondary,
        opacity: disabled ? 0.45 : 1,
        background: active ? R.accentSubtle : 'transparent',
        cursor: disabled ? 'default' : 'pointer',
        flex: '0 0 auto',
      }}
    >
      <Icon size={14} />
      {label && <span>{label}</span>}
    </button>
  );
}

/** Вертикальный разделитель между группами кнопок. */
export function ToolDivider() {
  return (
    <span
      style={{ width: 1, height: 18, background: R.border, margin: '0 4px', flex: '0 0 auto' }}
    />
  );
}

const COLOR_PALETTE = [
  // Фирменные цвета Redwood
  {
    title: 'Redwood',
    colors: ['#830c10', '#5c080b', '#a8171d', '#c92a2a', '#ff6b6b', '#ffe3e3'],
  },
  // Нейтральные и монохромные
  {
    title: 'Монохром',
    colors: ['#ffffff', '#f1f3f5', '#ced4da', '#868e96', '#343a40', '#000000'],
  },
  // Акцентные цвета
  {
    title: 'Акценты',
    colors: [
      '#2563eb', '#0ea5e9', '#16a34a', '#84cc16', '#eab308', '#f97316',
      '#9333ea', '#ec4899', '#0d9488', '#d97706', '#dc2626', '#4f46e5',
    ],
  },
];

/**
 * Пипетка и выбор цвета: фирменная палитра Redwood, прямой ввод HEX,
 * нативный селектор и сброс цвета.
 */
export function ColorButton({
  icon, value, title, disabled, onChange,
}: {
  icon: ReactNode;
  value?: string;
  title: string;
  disabled?: boolean;
  onChange: (value: string | undefined) => void;
}) {
  const [open, setOpen] = useState(false);
  const [customHex, setCustomHex] = useState(value ?? '');
  const ref = useRef<HTMLButtonElement>(null);
  const nativeRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setCustomHex(value ?? '');
  }, [value]);

  const handleApplyHex = (hex: string) => {
    let clean = hex.trim();
    if (!clean) return;
    if (!clean.startsWith('#')) clean = `#${clean}`;
    if (/^#[0-9a-fA-F]{3,8}$/.test(clean)) {
      onChange(clean);
      setOpen(false);
    }
  };

  return (
    <>
      <button
        ref={ref}
        title={title}
        disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
        className={`rt-toolbtn ${open ? 'rt-toolbtn--active' : ''}`}
        style={{
          display: 'inline-flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          width: 28,
          height: 28,
          color: disabled ? R.textMuted : R.textSecondary,
          opacity: disabled ? 0.45 : 1,
          background: open ? R.bgElevated : 'transparent',
          cursor: disabled ? 'default' : 'pointer',
          flex: '0 0 auto',
        }}
      >
        {icon}
        <span
          style={{
            width: 16,
            height: 3,
            background: value || 'transparent',
            border: value ? 'none' : `1px solid ${R.border}`,
          }}
        />
      </button>

      <AnchoredPopover anchorRef={ref} open={open} onClose={() => setOpen(false)}>
        <div
          style={{
            width: 204,
            padding: '10px 12px',
            background: R.bgPanel,
            border: `2px solid ${R.border}`,
            borderRadius: 6,
            boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
          }}
        >
          {/* Палитра Redwood */}
          <div style={{ fontSize: 10.5, fontWeight: 700, color: R.accentText, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
            Фирменные цвета Redwood
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 4, marginBottom: 10 }}>
            {COLOR_PALETTE[0].colors.map((color) => (
              <button
                key={color}
                title={color}
                onClick={() => {
                  onChange(color);
                  setOpen(false);
                }}
                style={{
                  height: 22,
                  background: color,
                  borderRadius: 3,
                  border: color.toLowerCase() === (value ?? '').toLowerCase() ? `2px solid ${R.accent}` : `1px solid ${R.border}`,
                  boxShadow: color.toLowerCase() === (value ?? '').toLowerCase() ? `0 0 0 1px ${R.accent}` : undefined,
                  cursor: 'pointer',
                }}
              />
            ))}
          </div>

          {/* Базовые цвета */}
          <div style={{ fontSize: 10.5, fontWeight: 600, color: R.textMuted, marginBottom: 5 }}>
            Базовые цвета
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 4, marginBottom: 10 }}>
            {COLOR_PALETTE[1].colors.map((color) => (
              <button
                key={color}
                title={color}
                onClick={() => {
                  onChange(color);
                  setOpen(false);
                }}
                style={{
                  height: 20,
                  background: color,
                  borderRadius: 3,
                  border: color.toLowerCase() === (value ?? '').toLowerCase() ? `2px solid ${R.accent}` : `1px solid ${R.border}`,
                  cursor: 'pointer',
                }}
              />
            ))}
          </div>

          {/* Акцентные цвета */}
          <div style={{ fontSize: 10.5, fontWeight: 600, color: R.textMuted, marginBottom: 5 }}>
            Палитра
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 4, marginBottom: 12 }}>
            {COLOR_PALETTE[2].colors.map((color) => (
              <button
                key={color}
                title={color}
                onClick={() => {
                  onChange(color);
                  setOpen(false);
                }}
                style={{
                  height: 20,
                  background: color,
                  borderRadius: 3,
                  border: color.toLowerCase() === (value ?? '').toLowerCase() ? `2px solid ${R.accent}` : `1px solid ${R.border}`,
                  cursor: 'pointer',
                }}
              />
            ))}
          </div>

          {/* Прямой ввод HEX и нативная пипетка */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
            <div style={{ position: 'relative', width: 28, height: 28, flex: '0 0 auto', borderRadius: 4, overflow: 'hidden', border: ft.edge }}>
              <input
                ref={nativeRef}
                type="color"
                value={value && value.startsWith('#') && value.length === 7 ? value : '#830c10'}
                onInput={(e) => {
                  const val = (e.target as HTMLInputElement).value;
                  setCustomHex(val);
                  onChange(val);
                }}
                onChange={(e) => {
                  const val = e.target.value;
                  setCustomHex(val);
                  onChange(val);
                }}
                style={{
                  position: 'absolute',
                  top: -6,
                  left: -6,
                  width: 40,
                  height: 40,
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                }}
              />
            </div>
            <input
              type="text"
              placeholder="#830c10"
              value={customHex}
              onChange={(e) => setCustomHex(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleApplyHex(customHex);
              }}
              style={{
                flex: 1,
                height: 28,
                padding: '0 8px',
                fontSize: 12,
                fontFamily: 'monospace',
                background: R.bgInput,
                border: ft.edge,
                borderRadius: 4,
                color: R.text,
                outline: 'none',
              }}
            />
            <button
              onClick={() => handleApplyHex(customHex)}
              style={{
                height: 28,
                padding: '0 8px',
                fontSize: 12,
                background: R.accent,
                color: R.onAccent,
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer',
                fontWeight: 600,
              }}
            >
              OK
            </button>
          </div>

          {/* Кнопка сброса цвета */}
          <button
            onClick={() => {
              onChange(undefined);
              setOpen(false);
            }}
            style={{
              width: '100%',
              height: 26,
              fontSize: 11.5,
              border: ft.edge,
              background: 'transparent',
              color: R.textMuted,
              borderRadius: 4,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            Сбросить цвет
          </button>
        </div>
      </AnchoredPopover>
    </>
  );
}
